// hooks/useAuth.js - VERSIONE HTTP-ONLY COOKIE CON SINCRONIZZAZIONE
import { useState, useCallback } from 'react';
import axios from 'axios';

// Importa l'event bus per sincronizzazione
import { authEventBus, AUTH_EVENTS } from '../../../utils/AuthEvents';

const useAuth = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || '/api';

    // ✅ Creazione istanza axios con configurazione per COOKIE HTTP-ONLY
    const apiClient = axios.create({
        baseURL: API_URL,
        withCredentials: true, // 🔥 CRITICO: Invia cookie automaticamente
        headers: {
            'Content-Type': 'application/json'
        },
    });

    // ✅ Helper per sincronizzare stato ed emettere eventi
    const syncAuthState = useCallback((newUser, newIsAuthenticated) => {
        /* console.log('🔄 Sincronizzazione stato auth:', { 
            user: newUser?.email_utente || null, 
            isAuthenticated: newIsAuthenticated 
        }); */
        
        setUser(newUser);
        setIsAuthenticated(newIsAuthenticated);
        
        // 🔥 Emetti evento per sincronizzare altri componenti
        authEventBus.emit(AUTH_EVENTS.AUTH_CHANGED, {
            user: newUser,
            isAuthenticated: newIsAuthenticated
        });
    }, []);

    // ✅ VERIFICA UTENTE AUTENTICATO (me) - VERSIONE HTTP-ONLY
    const getCurrentUser = useCallback(async () => {
        setLoading(true);
        setError(null);
    
        try {
            const response = await apiClient.get('/auth/me');
            
            if (response.data.success) {
                console.log('✅ Utente verificato dal backend:', response.data.user.email);
                
                // 🔥 AGGIORNA localStorage con i dati più recenti
                localStorage.setItem('user', JSON.stringify(response.data.user));
                
                syncAuthState(response.data.user, true);
                return {
                    success: true,
                    user: response.data.user,
                    isAuthenticated: true
                };
            }
            
            console.log('❌ Utente non autenticato dal backend');
            // 🔥 NON cancellare localStorage qui, lascia che il backend decida
            return { success: false, isAuthenticated: false };
            
        } catch (err) {
            console.error('❌ Errore verifica utente:', err.message);
            
            if (err.response?.status === 401) {
                console.log('⚠️ 401 - Sessione scaduta');
                syncAuthState(null, false);
                localStorage.removeItem('user'); // 🔥 Rimuovi solo su 401
                return { success: false, isAuthenticated: false };
            }
            
            return { success: false, isAuthenticated: false };
            
        } finally {
            setLoading(false);
        }
    }, [apiClient, syncAuthState]);

    // ✅ LOGIN UTENTE - VERSIONE CON TOKEN
    const login = useCallback(async (credentials) => {
        setLoading(true);
        setError(null);

        try {
            console.log('🔑 Tentativo login:', credentials.email);

            const response = await apiClient.post('/auth/login', credentials);

            if (response.data.success) {
                const { user, accessToken, refreshToken } = response.data;

                // ✅ SALVA IL TOKEN IN LOCALSTORAGE
                if (accessToken) {
                    localStorage.setItem('access_token', accessToken);
                    console.log('✅ Access Token salvato in localStorage');
                }
                if (refreshToken) {
                    localStorage.setItem('refresh_token', refreshToken);
                }

                // ✅ IMPOSTA L'HEADER PER LE PROSSIME RICHIESTE
                if (accessToken) {
                    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                    console.log('✅ Authorization header impostato');
                }

                // ✅ SALVA UTENTE
                localStorage.setItem('user', JSON.stringify(user));

                // ✅ Aggiorna lo stato
                syncAuthState(user, true);

                // ✅ Emetti eventi
                authEventBus.emit(AUTH_EVENTS.LOGIN, user);
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('cart:refresh'));
                }

                console.log('✅ Login completato con successo');

                return {
                    success: true,
                    user
                };
            }

            throw new Error('Login fallito');

        } catch (err) {
            console.error('🔥 Errore login:', err);
            const errorMessage = err.response?.data?.message || 'Credenziali non valide';
            setError(errorMessage);

            return {
                success: false,
                error: errorMessage
            };

        } finally {
            setLoading(false);
        }
    }, [apiClient, syncAuthState]); 

    // ✅ REGISTRAZIONE NUOVO UTENTE - CON VERIFICA EMAIL
    const register = useCallback(async (userData) => {
        setLoading(true);
        setError(null);
        
        try {
            console.log('📤 Invio dati registrazione:', { ...userData, password: '***' });
            
            const response = await apiClient.post('/auth/register', userData);
            
            console.log('📦 Risposta registrazione:', response.data);
            
            if (response.data.success) {
                // 🔥 CONTROLLA SE RICHIEDE VERIFICA EMAIL
                if (response.data.requiresVerification) {
                    console.log('📧 Registrazione richiede verifica email');
                    
                    // ✅ NON chiamare getCurrentUser perché l'utente non è ancora attivo
                    // ✅ NON sincronizzare stato di autenticazione
                    
                    return {
                        success: true,
                        requiresVerification: true,
                        message: response.data.message,
                        email: response.data.email
                    };
                }
                
                // Comportamento originale (senza verifica email)
                const { user } = response.data;
                
                // Verifica autenticazione immediata
                await getCurrentUser();
                
                return {
                    success: true,
                    user,
                    message: response.data.message,
                    requiresVerification: false
                };
            }
            
            throw new Error(response.data.message || 'Registrazione fallita');
            
        } catch (err) {
            console.error('🔥 Errore registrazione:', err);
            
            let errorMessage = 'Errore durante la registrazione';
            
            if (err.response) {
                errorMessage = err.response.data?.message || errorMessage;
            }
            
            setError(errorMessage);
            
            return {
                success: false,
                error: errorMessage,
                details: err.response?.data
            };
            
        } finally {
            setLoading(false);
        }
    }, [apiClient, getCurrentUser]);

    // ✅ - logout
    const logout = useCallback(async () => {
        try {
            console.log('🚪 Logout...');
            
            // ✅ Rimuovi il token dagli header axios
            delete axios.defaults.headers.common['Authorization'];
            
            // ✅ Rimuovi i token da localStorage
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            
            // ✅ Cancella i cookie manualmente (per sicurezza)
            document.cookie = 'session_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            document.cookie = 'user_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            
            // ✅ Chiama il logout del backend
            await apiClient.post('/auth/logout');
            
        } catch (err) {
            console.error('❌ Errore logout:', err);
        } finally {
            // ✅ Sincronizza lo stato
            syncAuthState(null, false);
            setError(null);
            
            // ✅ Emetti eventi per aggiornare i componenti
            authEventBus.emit(AUTH_EVENTS.LOGOUT);
            
            // ✅ Aggiorna il carrello
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('cart:refresh'));
            }
            
            console.log('✅ Logout completato (senza reload)');
        }
    }, [apiClient, syncAuthState]);

    // ✅ RECUPERA DATI UTENTE SPECIFICO (per admin) - VERSIONE HTTP-ONLY
    const getUserById = useCallback(async (userId) => {
        setLoading(true);
        setError(null);
        
        try {
            //console.log(`📋 Recupero dati utente ID: ${userId} (cookie HTTP-only)`);
            const response = await apiClient.get(`/admin/users/${userId}`);
            
            if (response.data.success) {
                return {
                    success: true,
                    user: response.data.user,
                    message: response.data.message
                };
            }
            
            throw new Error('Recupero dati utente fallito');
            
        } catch (err) {
            console.error('🔥 Errore recupero dati utente:', err);
            
            let errorMessage = 'Errore durante il recupero dei dati utente';
            
            if (err.response) {
                errorMessage = err.response.data?.message || errorMessage;
                
                if (err.response.status === 401) {
                    // Auto-reset su 401
                    syncAuthState(null, false);
                    errorMessage = 'Sessione scaduta';
                }
            }
            
            setError(errorMessage);
            
            return {
                success: false,
                error: errorMessage,
                statusCode: err.response?.status
            };
            
        } finally {
            setLoading(false);
        }
    }, [apiClient, syncAuthState]);

    // ✅ INIZIALIZZA AUTENTICAZIONE AL MOUNT - VERSIONE HTTP-ONLY
    const initializeAuth = useCallback(async () => {
        console.log('🔄 Inizializzazione auth...');
        
        // 🔥 EVITA LOOP: controlla se siamo già autenticati
        if (isAuthenticated && user) {
            console.log('✅ Già autenticato, skip initialize');
            return { success: true, user };
        }
        
        // Leggi da localStorage
        const storedUser = localStorage.getItem('user');
        if (storedUser && !isAuthenticated) {
            const parsedUser = JSON.parse(storedUser);
            console.log('📦 Utente trovato in localStorage:', parsedUser.email);
            setUser(parsedUser);
            setIsAuthenticated(true);
            return { success: true, user: parsedUser };
        }
        
        // Solo se non abbiamo user, chiama getCurrentUser
        if (!user) {
            await getCurrentUser();
        }
        
        return { success: isAuthenticated, user };
    }, [getCurrentUser, isAuthenticated, user]);

    // ✅ CAMBIA PASSWORD - VERSIONE HTTP-ONLY
    const changePassword = useCallback(async (currentPassword, newPassword) => {
        setLoading(true);
        
        try {
            const response = await apiClient.post('/users/change-password', {
                currentPassword,
                newPassword
            });
            
            if (response.data.success) {
                return { success: true, message: response.data.message };
            }
            
            throw new Error('Cambio password fallito');
            
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Errore cambio password';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [apiClient]);

    // ✅ AGGIORNA PROFILO - VERSIONE HTTP-ONLY
    const updateProfile = useCallback(async (profileData) => {
        setLoading(true);
        
        try {
            const response = await apiClient.put('/users/profile', profileData);
            
            if (response.data.success) {
                const updatedUser = { ...user, ...response.data.user };
                syncAuthState(updatedUser, true);
                
                // 🔥 Emetti evento specifico per aggiornamento profilo
                authEventBus.emit(AUTH_EVENTS.USER_UPDATED, updatedUser);
                
                return {
                    success: true,
                    user: updatedUser,
                    message: response.data.message
                };
            }
            
            throw new Error('Aggiornamento profilo fallito');
            
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Errore aggiornamento profilo';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoading(false);
        }
    }, [apiClient, user, syncAuthState]);

    // ✅ RESET ERROR
    const resetError = useCallback(() => {
        setError(null);
    }, []);

    // ✅ Funzione per sincronizzazione esterna (usata dai componenti per ricevere aggiornamenti)
    const syncExternalAuth = useCallback((externalUser, externalIsAuthenticated) => {
        /* console.log('📥 Ricevuta sincronizzazione esterna:', {
            user: externalUser?.email_utente || null,
            isAuthenticated: externalIsAuthenticated
        }); */
        
        // Aggiorna lo stato locale senza emettere eventi (per evitare loop)
        setUser(externalUser);
        setIsAuthenticated(externalIsAuthenticated);
    }, []);

    // ✅ Forza refresh dell'autenticazione
    const refreshAuth = useCallback(async () => {
        //console.log('🔄 Forzato refresh auth...');
        return await getCurrentUser();
    }, [getCurrentUser]);

    return {
        // Stato
        user,
        loading,
        error,
        isAuthenticated,
        
        // Azioni (quelle modificate per sincronizzazione)
        getUserById,
        register,
        login,
        logout,
        getCurrentUser,
        changePassword,
        updateProfile,
        initializeAuth,
        resetError,
        
        // Nuove funzioni per sincronizzazione
        syncExternalAuth,
        refreshAuth,
        
        // ⚠️ RIMUOVI hasToken() - non puoi verificare cookie HTTP-only
        // hasToken: () => !!localStorage.getItem('access_token')
    };
};

export default useAuth;
